// @i18n-ignore-file
import { createContext } from 'react'

import { CustomAttribute } from '@src/quince-site-common/src/domains/shopify/CustomAttribute'
import IShopifyCheckout from '@src/quince-site-common/src/domains/shopify/ShopifyCheckout'

import { CartLineItem, GiftingDetails, LineItems } from '../../cartService/cart.service.types'
import NewCheckoutService from '../NewCheckoutService'

export type CheckoutServiceType = NewCheckoutService
export type CheckoutServiceConstructor = typeof NewCheckoutService

export interface CheckoutContextState {
  initialized: boolean
  CheckoutService: CheckoutServiceConstructor
  checkoutInstance: CheckoutServiceType
}

const ERROR_MESSAGES = {
  CALCULATE_DISCOUNT_FOR_ALL_PRODUCUT_ERROR_MESSAGE:
    'calculateDiscountForAllProdcut must be called from within CheckoutContextProvider',
  RENDER_CUSTOM_ATTRIBUTE_CUSTOMIZATION_ERROR_MESSAGE:
    'renderCustomAttributeCustomization must be called from within CheckoutContextProvider',
  GET_CHECKOUT_ID_ERROR_MESSAGE: 'getCheckoutId must be called from within CheckoutContextProvider',
  TAKE_TO_CHECKOUT_ERROR_MESSAGE: 'takeToCheckout must be called from within CheckoutContextProvider',
  HANDLE_SHOPIFY_DISCOUNT_ERROR_MESSAGE: 'handleShopifyDiscount must be called from within CheckoutContextProvider',
  ADD_DISCOUNT_TO_CHECKOUT_ERROR_MESSAGE: 'addDiscountToCheckout must be called from within CheckoutContextProvider',
  INITIALIZE_CHECKOUT_ERROR_MESSAGE: 'initializeCheckout must be called from within CheckoutContextProvider',
  GET_CHECKOUT_ERROR_MESSAGE: 'getCheckout must be called from within CheckoutContextProvider',
  REMOVE_LINE_ITEM_ERROR_MESSAGE: 'removeLineItem must be called from within CheckoutContextProvider',
  REMOVE_LINE_ITEMS_ERROR_MESSAGE: 'removeLineItems must be called from within CheckoutContextProvider',
  UPDATE_LINE_ITEM_ERROR_MESSAGE: 'updateLineItem must be called from within CheckoutContextProvider',
  ADD_TO_CART_ERROR_MESSAGE: 'addToCart must be called from within CheckoutContextProvider',
  ADD_MULTIPLE_TO_CART_ERROR_MESSAGE: 'addMultipleToCart must be called from within CheckoutContextProvider',
  GET_CART_TOTAL_ITEMS_COUNT_ERROR_MESSAGE: 'getCartTotalItemsCount must be called from within CheckoutContextProvider',
  MERGE_CART_ERROR_MESSAGE: 'mergeCart must be called from within CheckoutContextProvider',
  BUY_NOW_CART_ERROR_MESSAGE: 'buyNowCart must be called from within CheckoutContextProvider',
  UPDATE_LINE_ITEMS_ERROR_MESSAGE: 'updateLineItems must be called from within CheckoutContextProvider',
  GET_PREVIOUS_CART_DATA_ERROR_MESSAGE: 'getPreviousCartData must be called from within CheckoutContextProvider',
  UPDATE_GIFTING_DETAILS_FOR_CART_ERROR_MESSAGE:
    'updateGiftingDetailsForCart must be called from within CheckoutContextProvider',
  UPDATE_GIFTING_FLAG_ERROR_MESSAGE: 'updateGiftingFlag must be called from within CheckoutContextProvider',
}

export const initialCheckoutContext: CheckoutContextState = {
  initialized: false,
  CheckoutService: {
    calculateDiscountForAllProdcut(_lineItem: CartLineItem): {
      price: number
      actualPrice: number
      discount: number
      amountPerQuantity: number
    } {
      throw new Error(ERROR_MESSAGES.CALCULATE_DISCOUNT_FOR_ALL_PRODUCUT_ERROR_MESSAGE)
    },
    renderCustomAttributeCustomization(_customAttributes: CustomAttribute[]): string | undefined {
      throw new Error(ERROR_MESSAGES.RENDER_CUSTOM_ATTRIBUTE_CUSTOMIZATION_ERROR_MESSAGE)
    },
  },
  checkoutInstance: {
    getCheckoutId: () => {
      throw new Error(ERROR_MESSAGES.GET_CHECKOUT_ID_ERROR_MESSAGE)
    },
    takeToCheckout: () => {
      throw new Error(ERROR_MESSAGES.TAKE_TO_CHECKOUT_ERROR_MESSAGE)
    },
    handleShopifyDiscount: () => {
      throw new Error(ERROR_MESSAGES.HANDLE_SHOPIFY_DISCOUNT_ERROR_MESSAGE)
    },
    addDiscountToCheckout: (_discountCode: string) => {
      throw new Error(ERROR_MESSAGES.ADD_DISCOUNT_TO_CHECKOUT_ERROR_MESSAGE)
    },
    initializeCheckout: () => {
      throw new Error(ERROR_MESSAGES.INITIALIZE_CHECKOUT_ERROR_MESSAGE)
    },
    getCheckout(_?: boolean) {
      throw new Error(ERROR_MESSAGES.GET_CHECKOUT_ERROR_MESSAGE)
    },
    removeLineItem: (_: string) => {
      throw new Error(ERROR_MESSAGES.REMOVE_LINE_ITEM_ERROR_MESSAGE)
    },
    removeLineItems: (_: string[]) => {
      throw new Error(ERROR_MESSAGES.REMOVE_LINE_ITEMS_ERROR_MESSAGE)
    },
    updateLineItem: (_lineItemId: string | null, _quantity: number, _adjustment: number) => {
      throw new Error(ERROR_MESSAGES.UPDATE_LINE_ITEM_ERROR_MESSAGE)
    },
    addToCart: (
      _shopifyVariantId: number,
      _quantity = 1,
      _customAttributes: CustomAttribute[] = [],
    ): Promise<IShopifyCheckout> => {
      throw new Error(ERROR_MESSAGES.ADD_TO_CART_ERROR_MESSAGE)
    },
    addMultipleToCart: (
      _variantDetails: {
        shopifyVariantId: number
        quantity: number
        customAttributes: CustomAttribute[]
      }[],
    ): Promise<IShopifyCheckout> => {
      throw new Error(ERROR_MESSAGES.ADD_MULTIPLE_TO_CART_ERROR_MESSAGE)
    },
    getCartTotalItemsCount: (): Promise<number> => {
      throw new Error(ERROR_MESSAGES.GET_CART_TOTAL_ITEMS_COUNT_ERROR_MESSAGE)
    },
    isInitialized: false,
    initializingPromise: null,
    mergeCart: (_cartId: string) => {
      throw new Error(ERROR_MESSAGES.MERGE_CART_ERROR_MESSAGE)
    },
    buyNowCart: (_variants: LineItems) => {
      throw new Error(ERROR_MESSAGES.BUY_NOW_CART_ERROR_MESSAGE)
    },
    updateLineItems: (
      _lineItems: {
        lineItemId: string | null | undefined
        adjustment: number
      }[],
    ) => {
      throw new Error(ERROR_MESSAGES.UPDATE_LINE_ITEMS_ERROR_MESSAGE)
    },
    getPreviousCartData: () => {
      throw new Error(ERROR_MESSAGES.GET_PREVIOUS_CART_DATA_ERROR_MESSAGE)
    },
    updateGiftingDetailsForCart: (_giftingDetails: GiftingDetails) => {
      throw new Error(ERROR_MESSAGES.UPDATE_GIFTING_DETAILS_FOR_CART_ERROR_MESSAGE)
    },
    updateGiftingFlag: (_isGift: boolean) => {
      throw new Error(ERROR_MESSAGES.UPDATE_GIFTING_FLAG_ERROR_MESSAGE)
    },
  },
}

const CheckoutContext = createContext<CheckoutContextState>(initialCheckoutContext)

// @i18n-ignore
const CHECKOUT_CONTEXT_DISPLAY_NAME = 'Checkout Context'
CheckoutContext.displayName = CHECKOUT_CONTEXT_DISPLAY_NAME

export default CheckoutContext
